import { Card, Space, Progress, Button, message, Tag, Modal, Spin } from "antd";
import { useContext, useEffect, useState, useCallback } from "react";
import { Uploader } from "@fastpix/resumable-uploads";
import { Context } from "../../context";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { getThumbnailUrl } from "../../utils/thumbnailUtils";
import { delay, fetchMovieContent, fetchSeriesContent } from "./utilis";

const { confirm } = Modal;

const TrailerUpload = ({ selectedContent, updateTrailer }) => {
  const [trailerContentExists, setTrailerContentExists] = useState(null);
  const [trailerContentImage, setTrailerContentImage] = useState();
  const [trailerUploading, setTrailerUploading] = useState(false);
  const [trailerUploadProgress, setTrailerUploadProgress] = useState(0);
  const [currentTrailerUploader, setCurrentTrailerUploader] = useState(null);
  const [loading, setLoading] = useState(false);

  const { token, apiUrl } = useContext(Context);

  const refreshTrailerContent = useCallback(async () => {
    try {
      await delay(3000);

      let updatedContent;
      if (selectedContent.contentType === "series_trailer") {
        updatedContent = await fetchSeriesContent({
          apiUrl,
          token,
          seriesId: selectedContent.contentId,
        });
      } else {
        updatedContent = await fetchMovieContent({
          apiUrl,
          token,
          contentId: selectedContent.contentId,
        });
      }

      if (updatedContent) {
        updateTrailer(updatedContent);
        setLoading(false);
      }
    } catch (error) {
      console.error("Failed to refresh trailer content:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedContent, apiUrl, token, updateTrailer]);

  useEffect(() => {
    const trailerStatus = selectedContent?.trailerStatusFp ?? null;
    let trailerContentStatus = null;

    if (trailerStatus === "processing") {
      trailerContentStatus = "processing";
    } else if (trailerStatus === "ready") {
      trailerContentStatus = "ready";
    }

    const img = getThumbnailUrl(selectedContent, "trailerImage");

    setTrailerContentImage(img);
    setTrailerContentExists(trailerContentStatus);
  }, [selectedContent]);

  const getPresignedUrl = async (params) => {
    try {
      const response = await fetch(`${apiUrl}/fpvideo/getPresignedUrl`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(params),
      });

      const data = await response.json();

      if (data.success) {
        return data.data.url;
      } else {
        message.error(data.error || "Failed to get presigned URL");
      }
    } catch (error) {
      message.error("Failed to get upload URL");
    }
  };

  const handleTrailerUpload = async (file) => {
    if (!selectedContent) {
      message.error("Please select content first");
      return;
    }

    try {
      setTrailerUploading(true);
      setTrailerUploadProgress(0);

      const trailerParams = {
        content_id: selectedContent.contentId,
        content_type: selectedContent.contentType,
        accessibility: "public",
        series_id: selectedContent.seriesId || null,
        season: selectedContent.season || null,
        episode: selectedContent.episode || null,
      };

      const signedUrl = await getPresignedUrl(trailerParams);

      const fileUploader = Uploader.init({
        endpoint: signedUrl,
        file: file,
        chunkSize: 153600,
        retryAttempts: 5,
      });

      setCurrentTrailerUploader(fileUploader);

      fileUploader.on("progress", (event) => {
        const percentage = Math.round(event.detail.progress);
        setTrailerUploadProgress(percentage);
      });

      fileUploader.on("success", () => {
        setTrailerUploadProgress(100);
        setTrailerUploading(false);
        setCurrentTrailerUploader(null);
        message.success("Trailer uploaded successfully!");

        // Start refresh process to get updated data
        setLoading(true);
        refreshTrailerContent();
        setTrailerContentExists("processing");
      });

      fileUploader.on("error", (event) => {
        setTrailerUploading(false);
        setCurrentTrailerUploader(null);
        message.error(
          "Trailer upload failed: " +
          (event?.detail?.message || "Unknown error")
        );
      });
    } catch (error) {
      setTrailerUploading(false);
      setCurrentTrailerUploader(null);
      message.error("Failed to start trailer upload");
    }
  };

  const handleDeleteTrailer = () => {
    if (!selectedContent) {
      message.error("Please select content first");
      return;
    }

    confirm({
      title: "Are you sure you want to delete this trailer?",
      content:
        "This action will permanently delete the trailer and cannot be undone.",
      icon: <ExclamationCircleFilled />,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {

          const params = new URLSearchParams({
            content_id: selectedContent.contentId,
            content_type: selectedContent.contentType,
            media_id_fp: selectedContent.mediaIdTrailerFp
          });

          const response = await fetch(
            `${apiUrl}/fpvideo/delete_content?${params.toString()}`,
            {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const result = await response.json();

          if (result.success) {
            message.success("Trailer deleted successfully!");

            setTrailerContentExists(null);
            setLoading(true);
            refreshTrailerContent();
          } else {
            message.error(result.message || "Failed to delete trailer");
          }
        } catch (error) {
          message.error("Failed to delete trailer");
        }
      },
    });
  };

  const pauseTrailerUpload = () => {
    if (currentTrailerUploader) {
      currentTrailerUploader.pause();
      message.info("Trailer Upload paused");
    }
  };

  const resumeTrailerUpload = () => {
    if (currentTrailerUploader) {
      currentTrailerUploader.resume();
      message.info("Trailer Upload resumed");
    }
  };

  const abortTrailerUpload = () => {
    if (currentTrailerUploader) {
      currentTrailerUploader.abort();
      setTrailerUploading(false);
      setTrailerUploadProgress(0);
      setCurrentTrailerUploader(null);
      message.info("Trailer Upload aborted");
    }
  };

  return (
    <Card title="Trailer (MP4 only)" size="default" style={{ width: 500 }}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '10px' }}>Updating Content...</div>
        </div>
      ) : (
        <Space direction="horizontal" size={30} style={{ width: "100%" }}>
          <img
            style={{ maxWidth: 360, maxHeight: 120 }}
            src={trailerContentImage}
            alt="trailerContentImage"
          />

          <Space direction="vertical" style={{ width: "100%" }}>
            {trailerContentExists === "ready" && (
              <>
                <Tag
                  color="green"
                  style={{
                    width: 160,
                    fontSize: 16,
                    marginTop: 20,
                    textAlign: "center",
                    padding: "5px",
                  }}
                >
                  Content Exists
                </Tag>

                <Button
                  type="primary"
                  htmlFor="mainContentUpload"
                  htmlType="submit"
                  loading={trailerUploading}
                  disabled={trailerUploading}
                  onClick={handleDeleteTrailer}
                >
                  Delete Content
                </Button>
              </>
            )}

            {trailerContentExists === "processing" && (
              <Tag
                color="green"
                style={{
                  width: 160,
                  fontSize: 16,
                  marginTop: 20,
                  marginBottom: 20,
                  textAlign: "center",
                  padding: "5px",
                }}
              >
                Content Processing
              </Tag>
            )}

            {trailerContentExists === "failed" && (
              <Tag
                color="red"
                style={{
                  width: 160,
                  fontSize: 16,
                  marginTop: 20,
                  marginBottom: 20,
                  textAlign: "center",
                  padding: "5px",
                }}
              >
                Content Failed, Try Re-uploading
              </Tag>
            )}

            <Button
              type="primary"
              htmlFor="mainContentUpload"
              htmlType="submit"
              loading={trailerUploading}
              disabled={trailerUploading}
              onClick={() =>
                document.getElementById("trailerContentUpload").click()
              }
            >
              {["ready", "processing"].includes(trailerContentExists)
                ? "Re-Upload Content"
                : "Upload Content"}
            </Button>
            <input
              type="file"
              id="trailerContentUpload"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  handleTrailerUpload(file);
                }
              }}
              accept="video/mp4"
              disabled={trailerUploadProgress}
              style={{ display: "none" }}
            />

            {trailerUploading && (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Progress percent={trailerUploadProgress} />
                <Space>
                  <Button size="small" onClick={pauseTrailerUpload}>
                    Pause
                  </Button>
                  <Button size="small" onClick={resumeTrailerUpload}>
                    Resume
                  </Button>
                  <Button size="small" danger onClick={abortTrailerUpload}>
                    Abort
                  </Button>
                </Space>
              </Space>
            )}
          </Space>
        </Space>
      )}

    </Card>
  );
};

export default TrailerUpload;
